import { CurrencyPipe, DatePipe, isPlatformServer, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatLuxonDateModule } from '@angular/material-luxon-adapter';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatCalendar, MatDatepickerModule, MatDateRangePicker } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { HelperFunctions, ICalendarDay } from '@curbnturf/entities';
import { dateStringAddDays, dateStringRangeToDateStringArray } from '@curbnturf/helpers';
import { DateTime } from 'luxon';
import { Subject } from 'rxjs';
import { IDatepickerStruct } from '../../../ng-bootstrap/datepicker/datepicker-struct';
import { CustomDatepickerAdapter } from '../custom-datepicker-adapter';
import { DateHeaderComponent } from '../date-header/date-header.component';
import debounce = HelperFunctions.debounce;

export const CUSTOM_DATE_FORMATS = {
  parse: {
    dateInput: 'LL',
  },
  display: {
    dateInput: 'DD',
    monthYearLabel: 'MMMM yyyy',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

@Component({
  selector: 'curbnturf-input-daterange',
  templateUrl: './input-daterange.component.html',
  styleUrls: ['./input-daterange.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    MatDatepickerModule,
    MatInputModule,
    ReactiveFormsModule,
    MatLuxonDateModule,
    MatButtonModule,
    MatDatepickerModule,
    NgClass,
    DatePipe,
  ],
  providers: [
    MatCalendar,
    MatDateRangePicker,
    CurrencyPipe,
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    { provide: DateAdapter, useClass: CustomDatepickerAdapter },
  ],
})
export class InputDaterangeComponent implements OnChanges, OnInit, OnDestroy {
  @Input() fromDate?: DateTime | null;
  @Input() toDate?: DateTime | null;
  @Input() displayMonths?: number;
  @Input() days: ICalendarDay[];
  @Input() minLength?: number;
  @Input() maxLength?: number;
  @Input() showInputs?: boolean;
  @Input() showNightsSelected?: boolean;
  @Input() errorMsg?: string;
  @Input() dropdownClass?: 'dropup-cnt' | 'dropdown-cnt' = 'dropdown-cnt';
  @Input() disabled: boolean = false;
  @Input() touchUi: boolean = false;

  @Output() dateRangeChanged = new EventEmitter();
  @Output() close = new EventEmitter();

  @ViewChild(MatDateRangePicker) datepicker: MatDateRangePicker<DateTime>;

  // private _calendar = inject<MatCalendar<DateTime>>(MatCalendar);
  private _destroyed = new Subject<void>();

  range: FormGroup<{
    start: FormControl<DateTime | undefined | null>;
    end: FormControl<DateTime | undefined | null>;
  }>;

  model: IDatepickerStruct;
  date: DateTime;
  today: DateTime;
  maxDate: DateTime;
  minDate: DateTime;

  daysHash: { [id: string]: ICalendarDay } = {}; // An array of all available dates

  error: string;
  numberOfNightsSelected?: number;

  calendarErrors: string[] = [];
  okEnabled = false;

  private readonly updateCalendarDebounced: () => Promise<void>;
  private readonly onDateSelectionDebounced: (date: DateTime) => Promise<void>;
  private availableDaysHash: { [id: string]: ICalendarDay };
  private endDaysHash: { [id: string]: ICalendarDay } = {};

  constructor(
    private currencyPipe: CurrencyPipe,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {
    this.showNightsSelected = this.showNightsSelected ? this.showNightsSelected : true;
    this.showInputs = this.showInputs ? this.showInputs : true;
    this.minLength = this.minLength ? this.minLength : 1;
    this.maxLength = this.maxLength ? this.maxLength : 365;

    this.today = DateTime.now().startOf('day');

    this.updateCalendarDebounced = debounce(() => this.updateCalendar(), isPlatformServer(this.platformId) ? 0 : 50);

    this.onDateSelectionDebounced = debounce(
      (date: DateTime) => this.onDateSelection(date),
      isPlatformServer(this.platformId) ? 0 : 50,
    );
  }

  ngOnInit() {
    // Absolute minimum date that can be set.
    this.minDate = DateTime.now().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

    this.range = new FormGroup({
      start: new FormControl(this.fromDate),
      end: new FormControl(this.toDate),
    });

    // Possibly all the info was initially sent to the object.
    this.checkEnabled();
  }

  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['days'] && changes['days'].currentValue) {
      // Re-populate the daysHash
      this.daysHash = {};
      this.days.forEach((day) => (this.daysHash[day.date] = day));

      // Re-populate the availableDaysHash
      this.availableDaysHash = {};
      this.days.forEach((day) => {
        const date = DateTime.fromISO(day.date);

        if (date && this.isDateReservable(date)) {
          this.availableDaysHash[day.date] = day;
        }
      });
    }
  }

  updateCalendar() {
    setTimeout(() => {
      // Custom Header Selector
      const monthYear: DateTime = DateTime.fromFormat(
        document.querySelector<HTMLTableCellElement>('.mat-calendar .calendar-header .header-label')?.innerText ?? '',
        CUSTOM_DATE_FORMATS.display.monthYearLabel,
      );

      const nextButton = document.querySelector<HTMLTableCellElement>(
        '.mat-calendar .calendar-header .calendar-header-next-month',
      );
      nextButton?.removeEventListener('click', async () => {
        await this.updateCalendarDebounced();
      });
      nextButton?.addEventListener('click', async () => {
        await this.updateCalendarDebounced();
      });

      const prevButton = document.querySelector<HTMLTableCellElement>(
        '.mat-calendar .calendar-header .calendar-header-previous-month',
      );
      prevButton?.removeEventListener('click', async () => {
        await this.updateCalendarDebounced();
      });
      prevButton?.addEventListener('click', async () => {
        await this.updateCalendarDebounced();
      });

      const cells = Array.from(
        document.querySelectorAll<HTMLDivElement>('.mat-calendar .mat-calendar-body-cell-content'),
      );

      cells.forEach((c) => {
        const date = parseInt(c.innerText, 10);
        const fullDate = monthYear.set({ day: date });
        const price = this.currencyPipe.transform(this.getPrice(fullDate), 'USD', 'symbol', '1.0-0');
        const text = date < 10 ? '0' + date.toString() : date.toString();
        if (price) {
          c.innerHTML = `<div class="row calendar-date-available"><div class="col-12 calendar-date">${text}</div><div class="col-12 calendar-price">${price}</div></div>`;
        } else if (this.isDateReservable(fullDate)) {
          c.innerHTML = `<div class="row calendar-date-available"><div class="col-12 calendar-date">${text}</div></div>`;
        } else {
          let extraClass: string = 'calendar-date-unavailable';
          const prevDate = fullDate.minus({ day: 1 });
          if (this.currencyPipe.transform(this.getPrice(prevDate), 'USD', 'symbol', '1.0-0')) {
            extraClass = ' partially-available';
          }
          c.innerHTML = `<div class="row ${extraClass}"><div class="col-12 calendar-date">${text}</div></div>`;
        }
        c.addEventListener('click', () => {
          this.onDateSelectionDebounced(fullDate).then();
        });
      });
    });
  }

  /**
   * Handle date range selection
   *
   * @param date
   */
  onDateSelection(date: DateTime) {
    this.okEnabled = false;

    if (!this.fromDate) {
      this.fromDate = date;
      this.toDate = undefined;
      this.calendarErrors = [];
    } else if (this.toDate && this.fromDate) {
      this.fromDate = date;
      this.toDate = undefined;
      this.calendarErrors = [];
    } else {
      if (date > this.fromDate) {
        if (this.toDate && date <= this.toDate) {
          this.fromDate = date;
          this.toDate = undefined;
          this.calendarErrors = [];
        } else {
          this.toDate = date;
        }
      } else {
        this.fromDate = date;
        this.toDate = undefined;
        this.calendarErrors = [];
      }
    }

    if (this.toDate && this.fromDate) {
      const rangeLength = this.toDate.diff(this.fromDate).as('days');
      if (this.minLength && rangeLength < this.minLength && rangeLength > 0) {
        const foundErrorIndex = this.calendarErrors.findIndex((error) => error === 'Some Dates Are Not Available');
        if (foundErrorIndex !== -1) {
          this.calendarErrors.splice(foundErrorIndex, 1, 'Reservation Is Too Short');
        } else {
          this.calendarErrors.push('Reservation Is Too Short');
        }
      }
      if (this.maxLength && rangeLength > this.maxLength) {
        const foundErrorIndex = this.calendarErrors.findIndex((error) => error === 'Some Dates Are Not Available');
        if (foundErrorIndex !== -1) {
          this.calendarErrors.splice(foundErrorIndex, 1, 'Reservation Length Is Too Long');
        } else {
          this.calendarErrors.push('Reservation Length Is Too Long');
        }
      }
    }

    this.calculateMaxEndDate();
    this.checkEnabled();

    if (this.fromDate && this.toDate && this.fromDate.isValid && this.toDate.isValid) {
      if (!this.isRangeAvailable(this.fromDate, this.toDate)) {
        this.calendarErrors.push('This date range is not available.');
      }
      this.numberOfNightsSelected = Math.round(this.toDate.diff(this.fromDate).as('days'));
    }

    // Recalculate the End Days on date selections.
    if (this.fromDate) {
      this.setEndDays(this.fromDate, this.toDate || this.fromDate);
    }

    if (this.calendarErrors.length === 0) {
      if (this.fromDate && this.toDate && this.fromDate.isValid && this.toDate.isValid) {
        this.okEnabled = true;
      }
      this.dateRangeChanged.emit({
        fromDate: this.fromDate,
        toDate: this.toDate,
      });
    }
  }

  calculateMaxEndDate() {
    if (this.fromDate) {
      this.setMaxEndDate(this.fromDate.plus({ days: this.maxLength }));
    } else {
      this.setMaxEndDate(DateTime.now().plus({ years: 1 }));
    }
  }

  setMaxEndDate(date?: DateTime) {
    // @todo get this from the listing max reservation
    this.maxDate = date ?? DateTime.now().plus({ years: 1 });
    this.updateCalendarDebounced();
  }

  setEndDays(fromDate?: DateTime, toDate?: DateTime) {
    // Re-populate the endDaysHash
    this.endDaysHash = {};
    let lastValidToDate;
    if (toDate && (this.maxLength || this.maxLength === 0)) {
      toDate = toDate.minus({ day: this.maxLength });

      if (fromDate) {
        // Find which toDates are available to select within a time radius of maxLength days
        for (let i = -this.maxLength; i < this.maxLength; i++) {
          if (this.isRangeAvailable(fromDate, toDate)) {
            const day = this.daysHash[toDate.toISODate() || ''];
            if (day) {
              this.endDaysHash[day.date] = day;
              lastValidToDate = day;
            }
          }
          toDate = toDate.plus({ day: 1 });
        }

        // Add the departure day
        if (lastValidToDate) {
          this.endDaysHash[dateStringAddDays(lastValidToDate.date, 1)] = {
            date: dateStringAddDays(lastValidToDate.date, 1),
          };
        }
      }
    } else if (toDate && !this.maxLength) {
      this.days.forEach((day) => {
        const dateObj = DateTime.fromISO(day.date);

        if (dateObj) {
          this.endDaysHash[day.date] = day;
        }
      });
    }
  }

  getPrice(date: DateTime) {
    if (this.availableDaysHash) {
      const day = this.availableDaysHash[date.toISODate() || ''];

      return day ? day.price : undefined;
    }

    return;
  }

  /**
   * Return true if the date is available and/or selectable
   *
   * @param date
   */
  isSelectable = (date: DateTime): boolean => {
    // If the available days are undefined then assume there are no restrictions
    if (this.days === undefined) {
      return true;
    }

    if (this.fromDate) {
      // if toDate is not set but fromDate is then make sure the date isn't within minLength of from date
      if (!this.toDate) {
        // we have to have year, month and day so we check first
        if (this.fromDate.isValid && date.isValid) {
          if (
            this.fromDate.plus({ days: this.minLength }) > date &&
            this.fromDate.minus({ days: this.minLength }) < date
          ) {
            return false;
          }
        }
      }

      // If the fromDate is already selected then use the endDaysHash
      return !!this.endDaysHash[date.toISODate() || ''];
    }

    if (this.availableDaysHash) {
      return !!this.availableDaysHash[date.toISODate() || ''];
    }

    return true;
  };

  isDisabled() {
    return (date: DateTime): boolean => {
      // If the available days are undefined then assume there are no restrictions
      if (this.days === undefined) {
        return false;
      }

      if (this.availableDaysHash) {
        return !this.availableDaysHash[date.toISODate() || ''];
      }

      return false;
    };
  }

  clearDates() {
    delete this.fromDate;
    delete this.toDate;
    this.numberOfNightsSelected = 0;
    this.endDaysHash = {};
    this.range.patchValue({
      start: null,
      end: null,
    });
    this.dateRangeChanged.emit({
      fromDate: this.fromDate,
      toDate: this.toDate,
    });
  }

  openDatePicker() {
    setTimeout(() => {
      document.querySelector('.mat-calendar-next-button')?.addEventListener('click', async () => {
        await this.updateCalendarDebounced();
      });
      document.querySelector('.mat-calendar-previous-button')?.addEventListener('click', async () => {
        await this.updateCalendarDebounced();
      });
    }, 50);

    this.maxDate = DateTime.now().plus({ years: 1 });
    setTimeout(() => {
      this.datepicker.open();
    });
  }

  private checkEnabled() {
    if (!this.calendarErrors || this.calendarErrors.length === 0) {
      if (this.fromDate && this.toDate && this.fromDate.isValid && this.toDate.isValid) {
        this.okEnabled = true;
      }
    }
    this.okEnabled = false;
  }

  /**
   * Validate that all nights between arriveDate and departDate are available
   *
   * @param arriveDate
   * @param departDate
   */
  private isRangeAvailable(arriveDate: DateTime, departDate: DateTime) {
    // If the available days are undefined, then assume there are no restrictions
    if (this.days === undefined) {
      return true;
    }

    if (arriveDate.equals(departDate)) {
      return !!this.daysHash[arriveDate.toISODate() || ''];
    }

    // Swap dates if departDate is before arriveDate
    if (arriveDate > departDate) {
      const swapDate = arriveDate;
      arriveDate = departDate;
      departDate = swapDate;
    }

    // Calculate the toDate from the departDate
    const toDate = departDate?.minus({ day: 1 });
    if (toDate && toDate >= arriveDate) {
      const dateRangeString = `${arriveDate.toISODate()}:${toDate.toISODate()}`;
      const dateArray = dateStringRangeToDateStringArray(dateRangeString);

      return !dateArray.some((dateInstance) => {
        return !this.daysHash[dateInstance];
      });
    }
    return;
  }

  private isDateReservable(date: DateTime) {
    const day = this.daysHash[date.toISODate() || ''];

    if (day) {
      // Check if theDay is reservable due to minLength restrictions
      let arriveDate = DateTime.fromISO(day.date);
      let departDate = DateTime.fromISO(day.date);
      if (this.minLength || this.minLength === 0) {
        departDate = departDate.plus({ day: this.minLength });
        for (let i = 0; i < this.minLength; i++) {
          // Filter out days that are not available due to minLength restrictions
          if (this.isRangeAvailable(arriveDate, departDate)) {
            return true;
          } else {
            arriveDate = arriveDate.minus({ day: 1 });
            departDate = departDate.minus({ day: 1 });
          }
        }
      }
    }
    return false;
  }

  protected readonly DateHeaderComponent = DateHeaderComponent;
}
